import type {
  Character,
  EvoLinkCategory,
  ExportScope,
  FilmProject,
  PackageSourceMapFrame,
  PackageSourceMapLaneItem,
  PackageSourceMapSnapshot,
  Scene,
  StoryboardFrame,
  UploadedAssetRecord
} from "../types";
import { frameStatus, formatRuntime, sceneChunks, scenesForScope } from "./storyboard";

export type PackageSourceMapDiffTone = "neutral" | "added" | "removed" | "changed" | "blocked";
export type PackageSourceMapDiffState = "no_baseline" | "unchanged" | "changed" | "blocked";

export interface PackageSourceMapDiffRow {
  id: string;
  label: string;
  value: string;
  delta: string;
  detail: string;
  changed: boolean;
  tone: PackageSourceMapDiffTone;
}

export interface PackageSourceMapDiff {
  baselineAvailable: boolean;
  state: PackageSourceMapDiffState;
  changedCount: number;
  summary: string;
  rows: PackageSourceMapDiffRow[];
  raw: Record<string, unknown>;
}

function characterCardItem(character: Character | undefined, name: string): PackageSourceMapLaneItem {
  if (!character) {
    return {
      label: name,
      detail: "Missing character card",
      source: "missing",
      status: "missing"
    };
  }
  if (character.cardDisabled) {
    return {
      id: character.id,
      label: character.name,
      detail: "Character card disabled",
      source: "disabled",
      status: "disabled"
    };
  }
  if (character.cardSource === "seed_generated" || character.cardStatus === "generated") {
    return {
      id: character.id,
      label: character.name,
      detail: character.cardAssetLabel || "Seedance generated character card",
      source: "seedance",
      status: "generated"
    };
  }
  if (character.cardStatus === "uploaded") {
    return {
      id: character.id,
      label: character.name,
      detail: character.cardAssetLabel || "Uploaded character card",
      source: "user_upload",
      status: "uploaded"
    };
  }
  return {
    id: character.id,
    label: character.name,
    detail: "Missing character card",
    source: character.cardSource || "missing",
    status: "missing"
  };
}

function voiceItem(character: Character | undefined, name: string): PackageSourceMapLaneItem {
  if (!character) {
    return {
      label: name,
      detail: "Missing voice",
      source: "missing",
      status: "missing"
    };
  }
  if (character.voiceDisabled) {
    return {
      id: character.id,
      label: character.name,
      detail: "Voice disabled",
      source: "disabled",
      status: "disabled"
    };
  }
  if (character.voiceStatus === "not_needed") {
    return {
      id: character.id,
      label: character.name,
      detail: "No voice required",
      source: "not_needed",
      status: "not_needed"
    };
  }
  if (character.voiceSource === "user_upload") {
    return {
      id: character.id,
      label: character.name,
      detail: character.voiceAssetLabel || "User audio",
      source: "user_upload",
      status: character.voiceStatus === "assigned" ? "assigned" : "missing"
    };
  }
  if (character.voiceSource === "seed_generated") {
    return {
      id: character.id,
      label: character.name,
      detail: character.voicePreset || "Seedance generated voice",
      source: "seedance",
      status: character.voiceStatus === "assigned" ? "assigned" : "missing"
    };
  }
  return {
    id: character.id,
    label: character.name,
    detail: character.voiceStatus === "assigned" ? "Assigned voice" : "Missing voice",
    source: character.voiceSource || "missing",
    status: character.voiceStatus
  };
}

function referenceItem(asset: UploadedAssetRecord | undefined, assetId: string): PackageSourceMapLaneItem {
  if (!asset) {
    return {
      id: assetId,
      label: assetId,
      detail: "Missing scene reference",
      source: "missing",
      status: "missing"
    };
  }
  if (asset.disabled) {
    return {
      id: asset.assetId,
      label: asset.label || asset.fileName || assetId,
      detail: "Reference disabled",
      source: "disabled",
      status: "disabled"
    };
  }
  const generated = asset.sourceMode === "seed_generated"
    || asset.provider === "evolink"
    || asset.provider === "seedance"
    || asset.provider === "seed";
  return {
    id: asset.assetId,
    label: asset.label || asset.fileName || assetId,
    detail: asset.parseStatus === "parsed" ? "Parsed reference board" : asset.fileName || "Scene reference",
    source: generated ? "seedance" : "user_upload",
    status: generated ? "generated" : asset.parseStatus === "parsed" ? "parsed" : "uploaded"
  };
}

function excludedLane(label: string): PackageSourceMapLaneItem[] {
  return [{
    label,
    detail: "Pass-through category disabled",
    source: "excluded",
    status: "excluded"
  }];
}

function sceneReferenceIds(project: FilmProject, sceneId: string): string[] {
  return (project.uploadedAssets?.sceneReferences || [])
    .filter((asset) => asset.sceneId === sceneId && !asset.disabled)
    .map((asset) => asset.assetId)
    .filter((assetId): assetId is string => Boolean(assetId));
}

function sceneVoiceNames(project: FilmProject, scene: Scene): string[] {
  return scene.characters.filter((name) => {
    const character = project.characters.find((item) => item.name === name);
    return Boolean(character && character.voiceStatus !== "not_needed" && !character.voiceDisabled);
  });
}

function assignmentNames(frame: StoryboardFrame, scene: Scene, project: FilmProject) {
  return {
    characterNames: frame.sourceAssignments?.characterNames || scene.characters,
    voiceCharacterNames: frame.sourceAssignments?.voiceCharacterNames || sceneVoiceNames(project, scene),
    referenceAssetIds: frame.sourceAssignments?.referenceAssetIds || sceneReferenceIds(project, scene.id)
  };
}

function buildFrameSnapshot(
  project: FilmProject,
  scene: Scene,
  frame: StoryboardFrame,
  enabledCategories: Set<EvoLinkCategory>,
  charactersByName: Map<string, Character>,
  referencesById: Map<string, UploadedAssetRecord>
): PackageSourceMapFrame {
  const assignments = assignmentNames(frame, scene, project);
  const chunk = sceneChunks(scene).find((item) => item.frameIds.includes(frame.id));
  const boardsEnabled = enabledCategories.has("boards");
  return {
    frameId: frame.id,
    label: frame.label,
    shotType: frame.shotType,
    status: boardsEnabled ? frameStatus(frame) : "Boards excluded",
    imageUrl: frame.imageUrl,
    source: frame.source,
    enabled: boardsEnabled && !frame.disabled,
    locked: frame.locked,
    queuedRegenerate: frame.queuedRegenerate,
    generationStatus: frame.generationStatus,
    assignmentMode: frame.sourceAssignments ? "board_override" : "scene_defaults",
    characters: enabledCategories.has("characters")
      ? assignments.characterNames.map((name) => characterCardItem(charactersByName.get(name), name))
      : excludedLane("Characters excluded"),
    voices: enabledCategories.has("voices")
      ? assignments.voiceCharacterNames.map((name) => voiceItem(charactersByName.get(name), name))
      : excludedLane("Voices excluded"),
    references: enabledCategories.has("locations")
      ? assignments.referenceAssetIds.map((assetId) => referenceItem(referencesById.get(assetId), assetId))
      : excludedLane("References excluded"),
    look: {
      style: enabledCategories.has("style") ? project.stylePreset : "Style excluded",
      colorGrade: enabledCategories.has("colorGrade") ? project.colorGrade : "Color grade excluded",
      aspect: enabledCategories.has("aspect") ? project.aspectRatio : "Aspect excluded",
      resolution: enabledCategories.has("aspect") ? project.resolution : "Resolution excluded"
    },
    prompt: enabledCategories.has("lines") ? frame.prompt || "No frame prompt" : "Lines excluded",
    blockingPlan: frame.blockingPlan,
    timing: chunk && enabledCategories.has("chunkTiming")
      ? {
          chunkId: chunk.id,
          label: chunk.label,
          durationSeconds: chunk.durationSeconds,
          runtime: formatRuntime(chunk.durationSeconds),
          locked: chunk.locked
        }
      : {
          label: enabledCategories.has("chunkTiming") ? "Frame timing" : "Chunk timing excluded",
          durationSeconds: enabledCategories.has("chunkTiming") ? frame.durationSeconds : 0,
          runtime: enabledCategories.has("chunkTiming") ? formatRuntime(frame.durationSeconds) : "Excluded",
          locked: false
        }
  };
}

function laneCount(frames: PackageSourceMapFrame[], lane: "characters" | "voices" | "references"): number {
  return frames.reduce(
    (total, frame) => total + frame[lane].filter((item) => item.status !== "excluded").length,
    0
  );
}

export function buildPackageSourceMapSnapshot(
  project: FilmProject,
  selectedSceneId: string,
  scope: ExportScope,
  categories: EvoLinkCategory[],
  createdAt = new Date().toISOString()
): PackageSourceMapSnapshot {
  const enabledCategories = new Set(categories);
  const charactersByName = new Map(project.characters.map((character) => [character.name, character]));
  const referencesById = new Map(
    (project.uploadedAssets?.sceneReferences || [])
      .filter((asset) => asset.assetId)
      .map((asset) => [asset.assetId as string, asset])
  );
  const scenes = scenesForScope(project, selectedSceneId, scope).map((scene) => {
    const frames = scene.frames.map((frame) =>
      buildFrameSnapshot(project, scene, frame, enabledCategories, charactersByName, referencesById)
    );
    return {
      sceneId: scene.id,
      sceneNumber: scene.number,
      slugline: scene.slugline,
      runtimeSeconds: scene.durationSeconds,
      includedBoardCount: frames.filter((frame) => frame.enabled).length,
      frames
    };
  });
  const allFrames = scenes.flatMap((scene) => scene.frames);
  const chunks = scenesForScope(project, selectedSceneId, scope).flatMap((scene) =>
    sceneChunks(scene).filter((chunk) => chunk.enabled)
  );
  const runtimeSeconds = chunks.length
    ? chunks.reduce((total, chunk) => total + chunk.durationSeconds, 0)
    : scenes.reduce((total, scene) => total + scene.runtimeSeconds, 0);
  return {
    schema: "kinetix_film.package_source_map.v1",
    createdAt,
    scope,
    sceneIds: scenes.map((scene) => scene.sceneId),
    categories,
    totals: {
      sceneCount: scenes.length,
      boardCount: allFrames.length,
      includedBoardCount: allFrames.filter((frame) => frame.enabled).length,
      customBoardOverrideCount: allFrames.filter((frame) => frame.assignmentMode === "board_override").length,
      characterAssignmentCount: laneCount(allFrames, "characters"),
      voiceAssignmentCount: laneCount(allFrames, "voices"),
      referenceAssignmentCount: laneCount(allFrames, "references"),
      chunkCount: enabledCategories.has("chunkTiming") ? chunks.length : 0,
      runtimeSeconds
    },
    scenes
  };
}

export function packageSourceMapFromPackage(sourcePackage: Record<string, unknown>): PackageSourceMapSnapshot | undefined {
  const sourceMap = sourcePackage.sourceMap;
  if (!sourceMap || typeof sourceMap !== "object" || Array.isArray(sourceMap)) return undefined;
  if ((sourceMap as { schema?: unknown }).schema !== "kinetix_film.package_source_map.v1") return undefined;
  return sourceMap as PackageSourceMapSnapshot;
}

function setDiff(current: string[], previous: string[]) {
  const currentSet = new Set(current);
  const previousSet = new Set(previous);
  return {
    added: current.filter((item) => !previousSet.has(item)),
    removed: previous.filter((item) => !currentSet.has(item))
  };
}

function joinPreview(values: string[], empty = "No changes"): string {
  if (!values.length) return empty;
  if (values.length <= 4) return values.join(" · ");
  return `${values.slice(0, 4).join(" · ")} · +${values.length - 4}`;
}

function deltaCount(current: number, previous: number, suffix = ""): string {
  const delta = current - previous;
  if (delta === 0) return "No change";
  return `${delta > 0 ? "+" : ""}${delta}${suffix}`;
}

function deltaTone(current: number, previous: number): PackageSourceMapDiffTone {
  if (current === previous) return "neutral";
  return current > previous ? "added" : "removed";
}

function countRow(
  id: string,
  label: string,
  current: number,
  previous: number,
  detail: string,
  suffix = ""
): PackageSourceMapDiffRow {
  const changed = current !== previous;
  return {
    id,
    label,
    value: `${current}${suffix}`,
    delta: deltaCount(current, previous, suffix),
    detail,
    changed,
    tone: deltaTone(current, previous)
  };
}

function signatureList(values: PackageSourceMapLaneItem[]): string {
  return values
    .filter((item) => item.status !== "excluded")
    .map((item) => `${item.label}:${item.source}:${item.status}:${item.detail}`)
    .sort()
    .join("|");
}

function lookSignature(frame: PackageSourceMapFrame): string {
  return `${frame.look.style}|${frame.look.colorGrade}|${frame.look.aspect}|${frame.look.resolution}`;
}

function stateSignature(frame: PackageSourceMapFrame): string {
  return `${frame.enabled}|${frame.source}|${frame.status}|${frame.locked}|${frame.queuedRegenerate}|${frame.generationStatus || ""}`;
}

function blockingSignature(frame: PackageSourceMapFrame): string {
  const blocking = frame.blockingPlan;
  if (!blocking) return "";
  const marks = (blocking.characterMarks || [])
    .map((mark) => `${mark.character}:${mark.framePosition}:${mark.depth || ""}:${mark.eyelineTarget || ""}:${mark.action || ""}`)
    .sort()
    .join("|");
  return [
    marks,
    blocking.objectMark,
    blocking.movementPath,
    blocking.cameraAxis,
    blocking.screenDirection,
    blocking.startFrame,
    blocking.endFrame,
    blocking.storyChange,
    blocking.continuityRisk
  ].join("|");
}

function flattenFrames(snapshot: PackageSourceMapSnapshot): Map<string, PackageSourceMapFrame> {
  const frames = new Map<string, PackageSourceMapFrame>();
  for (const scene of snapshot.scenes) {
    for (const frame of scene.frames) frames.set(frame.frameId, frame);
  }
  return frames;
}

function changedFrameLabels(
  current: PackageSourceMapSnapshot,
  previous: PackageSourceMapSnapshot,
  signature: (frame: PackageSourceMapFrame) => string
): string[] {
  const previousFrames = flattenFrames(previous);
  return current.scenes.flatMap((scene) =>
    scene.frames
      .filter((frame) => {
        const previousFrame = previousFrames.get(frame.frameId);
        return !previousFrame || signature(frame) !== signature(previousFrame);
      })
      .map((frame) => `${String(scene.sceneNumber).padStart(2, "0")} ${frame.shotType}`)
  );
}

function blockerRow(currentBlockers: string[], previousBlockers: string[]): PackageSourceMapDiffRow {
  const changed = currentBlockers.length !== previousBlockers.length
    || setDiff(currentBlockers, previousBlockers).added.length > 0
    || setDiff(currentBlockers, previousBlockers).removed.length > 0;
  const diff = setDiff(currentBlockers, previousBlockers);
  return {
    id: "review_blockers",
    label: "Review blockers",
    value: `${currentBlockers.length} open`,
    delta: changed ? deltaCount(currentBlockers.length, previousBlockers.length) : "No change",
    detail: changed
      ? [
          diff.added.length ? `New: ${joinPreview(diff.added)}` : "",
          diff.removed.length ? `Resolved: ${joinPreview(diff.removed)}` : ""
        ].filter(Boolean).join(" · ")
      : currentBlockers.length ? joinPreview(currentBlockers, "No blockers") : "No blockers",
    changed,
    tone: currentBlockers.length ? "blocked" : "neutral"
  };
}

export function diffPackageSourceMaps(
  current: PackageSourceMapSnapshot,
  previous?: PackageSourceMapSnapshot,
  options: { currentBlockers?: string[]; previousBlockers?: string[] } = {}
): PackageSourceMapDiff {
  if (!previous) {
    return {
      baselineAvailable: false,
      state: "no_baseline",
      changedCount: 0,
      summary: "No saved package snapshot yet",
      rows: [{
        id: "baseline",
        label: "Baseline",
        value: "None",
        delta: "Save package",
        detail: "Save a dry-run package to create the first comparison point.",
        changed: false,
        tone: "neutral"
      }],
      raw: { current, previous: null, currentBlockers: options.currentBlockers || [] }
    };
  }

  const sceneDiff = setDiff(current.sceneIds, previous.sceneIds);
  const categoryDiff = setDiff(current.categories, previous.categories);
  const characterChanges = changedFrameLabels(current, previous, (frame) => signatureList(frame.characters));
  const voiceChanges = changedFrameLabels(current, previous, (frame) => signatureList(frame.voices));
  const referenceChanges = changedFrameLabels(current, previous, (frame) => signatureList(frame.references));
  const lookChanges = changedFrameLabels(current, previous, lookSignature);
  const boardStateChanges = changedFrameLabels(current, previous, stateSignature);
  const blockingChanges = changedFrameLabels(current, previous, blockingSignature);
  const rows: PackageSourceMapDiffRow[] = [
    {
      id: "scene_scope",
      label: "Scene scope",
      value: `${current.totals.sceneCount} scene${current.totals.sceneCount === 1 ? "" : "s"}`,
      delta: sceneDiff.added.length || sceneDiff.removed.length
        ? `+${sceneDiff.added.length} / -${sceneDiff.removed.length}`
        : "No change",
      detail: sceneDiff.added.length || sceneDiff.removed.length
        ? [
            sceneDiff.added.length ? `Added: ${joinPreview(sceneDiff.added)}` : "",
            sceneDiff.removed.length ? `Removed: ${joinPreview(sceneDiff.removed)}` : ""
          ].filter(Boolean).join(" · ")
        : "Same scene set as latest package.",
      changed: sceneDiff.added.length > 0 || sceneDiff.removed.length > 0,
      tone: sceneDiff.added.length ? "added" : sceneDiff.removed.length ? "removed" : "neutral"
    },
    countRow(
      "included_boards",
      "Included boards",
      current.totals.includedBoardCount,
      previous.totals.includedBoardCount,
      `${current.totals.boardCount} total board slots in current scope.`
    ),
    {
      id: "board_states",
      label: "Board states",
      value: `${boardStateChanges.length} changed`,
      delta: boardStateChanges.length ? "Changed" : "No change",
      detail: boardStateChanges.length ? joinPreview(boardStateChanges) : "Uploaded, locked, disabled, and generated states match.",
      changed: boardStateChanges.length > 0,
      tone: boardStateChanges.length ? "changed" : "neutral"
    },
    {
      id: "character_cards",
      label: "Character cards",
      value: `${current.totals.characterAssignmentCount} assignments`,
      delta: characterChanges.length ? `${characterChanges.length} board${characterChanges.length === 1 ? "" : "s"}` : "No change",
      detail: characterChanges.length ? joinPreview(characterChanges) : "Character card assignments match.",
      changed: characterChanges.length > 0,
      tone: characterChanges.length ? "changed" : "neutral"
    },
    {
      id: "voices",
      label: "Voices",
      value: `${current.totals.voiceAssignmentCount} assignments`,
      delta: voiceChanges.length ? `${voiceChanges.length} board${voiceChanges.length === 1 ? "" : "s"}` : "No change",
      detail: voiceChanges.length ? joinPreview(voiceChanges) : "Voice assignments match.",
      changed: voiceChanges.length > 0,
      tone: voiceChanges.length ? "changed" : "neutral"
    },
    {
      id: "references",
      label: "References",
      value: `${current.totals.referenceAssignmentCount} assignments`,
      delta: referenceChanges.length ? `${referenceChanges.length} board${referenceChanges.length === 1 ? "" : "s"}` : "No change",
      detail: referenceChanges.length ? joinPreview(referenceChanges) : "Scene/reference assignments match.",
      changed: referenceChanges.length > 0,
      tone: referenceChanges.length ? "changed" : "neutral"
    },
    {
      id: "blocking",
      label: "Blocking",
      value: `${blockingChanges.length} changed`,
      delta: blockingChanges.length ? "Changed" : "No change",
      detail: blockingChanges.length ? joinPreview(blockingChanges) : "Character marks, movement, camera axis, and end-frame states match.",
      changed: blockingChanges.length > 0,
      tone: blockingChanges.length ? "changed" : "neutral"
    },
    {
      id: "look",
      label: "Look",
      value: `${current.categories.includes("style") ? "Style" : "No style"} · ${current.categories.includes("colorGrade") ? "Grade" : "No grade"}`,
      delta: lookChanges.length ? "Changed" : "No change",
      detail: lookChanges.length ? joinPreview(lookChanges) : "Style, color grade, aspect, and resolution match.",
      changed: lookChanges.length > 0,
      tone: lookChanges.length ? "changed" : "neutral"
    },
    {
      id: "runtime_chunks",
      label: "Runtime / chunks",
      value: `${formatRuntime(current.totals.runtimeSeconds)} · ${current.totals.chunkCount} chunks`,
      delta: [
        deltaCount(current.totals.runtimeSeconds, previous.totals.runtimeSeconds, "s"),
        deltaCount(current.totals.chunkCount, previous.totals.chunkCount)
      ].filter((item) => item !== "No change").join(" · ") || "No change",
      detail: "Compares current scope runtime and enabled chunk count.",
      changed: current.totals.runtimeSeconds !== previous.totals.runtimeSeconds || current.totals.chunkCount !== previous.totals.chunkCount,
      tone: current.totals.runtimeSeconds !== previous.totals.runtimeSeconds || current.totals.chunkCount !== previous.totals.chunkCount ? "changed" : "neutral"
    },
    {
      id: "pass_through",
      label: "Pass-through",
      value: `${current.categories.length} enabled`,
      delta: categoryDiff.added.length || categoryDiff.removed.length
        ? `+${categoryDiff.added.length} / -${categoryDiff.removed.length}`
        : "No change",
      detail: categoryDiff.added.length || categoryDiff.removed.length
        ? [
            categoryDiff.added.length ? `Added: ${joinPreview(categoryDiff.added)}` : "",
            categoryDiff.removed.length ? `Removed: ${joinPreview(categoryDiff.removed)}` : ""
          ].filter(Boolean).join(" · ")
        : "Same pass-through categories.",
      changed: categoryDiff.added.length > 0 || categoryDiff.removed.length > 0,
      tone: categoryDiff.added.length ? "added" : categoryDiff.removed.length ? "removed" : "neutral"
    },
    blockerRow(options.currentBlockers || [], options.previousBlockers || [])
  ];
  const changedCount = rows.filter((row) => row.changed).length;
  const currentBlockerCount = (options.currentBlockers || []).length;
  const state: PackageSourceMapDiffState = currentBlockerCount
    ? "blocked"
    : changedCount
      ? "changed"
      : "unchanged";
  return {
    baselineAvailable: true,
    state,
    changedCount,
    summary: state === "blocked"
      ? `${currentBlockerCount} review blocker${currentBlockerCount === 1 ? "" : "s"} still open`
      : changedCount
        ? `${changedCount} package change${changedCount === 1 ? "" : "s"} since latest save`
        : "No package changes since latest save",
    rows,
    raw: {
      current,
      previous,
      state,
      currentBlockers: options.currentBlockers || [],
      previousBlockers: options.previousBlockers || [],
      changedRows: rows.filter((row) => row.changed).map((row) => row.id)
    }
  };
}
